import { Campaign } from "model/campaign";
import { CampaignDisplayCategory } from "model/campaign-display-category";

/**
 * MAX-3878 — grouped campaigns display.
 *
 * Pure, state-free grouping/ordering/counting logic for the Campaigns page accordions.
 * Kept out of selector.ts and the page component so it can be unit-tested directly with
 * plain Campaign[] / CampaignDisplayCategory[] inputs (RTL render tests are broken repo-wide),
 * and so counts + total are single-sourced (the selector and the render both read this).
 */

/** Sentinel group id for the synthesized Uncategorized bucket (null FK). Not a real row. */
export const UNCATEGORIZED_ID = -1;

/** Display name for the synthesized Uncategorized accordion. */
export const UNCATEGORIZED_NAME = "Uncategorized";

export interface CampaignCategoryGroup {
	/** Real category id, or UNCATEGORIZED_ID for the synthesized null-FK bucket. */
	id: number;
	name: string;
	campaigns: Campaign[];
	count: number;
	/** True only for the synthesized Uncategorized bucket (pinned last, virtual). */
	uncategorized: boolean;
}

export interface GroupedDripCampaigns {
	groups: CampaignCategoryGroup[];
	/** FE-derived total across all groups (== campaigns.length). Single source of truth. */
	total: number;
}

/** Default category for brand-new campaigns (Q1). Matched by name, not a magic id. */
export const DEFAULT_CATEGORY_NAME = "General";

/** Alphabetical by name (AM3 — no displayOrder). Locale-aware, case-insensitive. */
export const compareCategoryName = (a: CampaignDisplayCategory, b: CampaignDisplayCategory) =>
	a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

/** Real categories sorted alphabetically by name (AM3). Does not include Uncategorized. */
export const sortCategoriesAlphabetically = (
	categories: CampaignDisplayCategory[],
): CampaignDisplayCategory[] => [...categories].sort(compareCategoryName);

/**
 * Group drip campaigns under their display categories.
 *
 * - Outer loop = the BE categories list (so EMPTY categories still render).
 * - Categories are sorted ALPHABETICALLY by name (AM3); the Uncategorized bucket
 *   (campaigns with a null/undefined FK) is synthesized and pinned LAST.
 * - Grouping happens AFTER any upstream sort/mutation of `campaigns`; per-campaign
 *   order within a group is preserved from the input (getAllDripCampaigns sorts by name).
 * - Counts and total are derived here so the render never re-computes them (no drift).
 */
export const groupDripCampaigns = (
	categories: CampaignDisplayCategory[],
	campaigns: Campaign[],
): GroupedDripCampaigns => {
	const byCategoryId = new Map<number, Campaign[]>();
	for (const campaign of campaigns) {
		const key = campaign.campaignDisplayCategoryId ?? UNCATEGORIZED_ID;
		const bucket = byCategoryId.get(key);
		if (bucket) {
			bucket.push(campaign);
		} else {
			byCategoryId.set(key, [campaign]);
		}
	}

	const groups: CampaignCategoryGroup[] = sortCategoriesAlphabetically(categories).map(
		(category) => {
			const list = byCategoryId.get(category.id) ?? [];
			return {
				id: category.id,
				name: category.name,
				campaigns: list,
				count: list.length,
				uncategorized: false,
			};
		},
	);

	// Uncategorized = null-FK campaigns, synthesized, pinned last (after the alphabetical reals).
	// Hidden when EMPTY (figma 1013:16450): it's the neutral migration sink / leftovers bucket, NOT
	// a seeded row — it surfaces only when it actually holds campaigns. (Real categories still render
	// empty with a "0 Campaigns" count; Uncategorized is the deliberate exception.)
	const uncategorized = byCategoryId.get(UNCATEGORIZED_ID) ?? [];
	if (uncategorized.length > 0) {
		groups.push({
			id: UNCATEGORIZED_ID,
			name: UNCATEGORIZED_NAME,
			campaigns: uncategorized,
			count: uncategorized.length,
			uncategorized: true,
		});
	}

	return { groups, total: campaigns.length };
};

/** The client's "General" category row (Q1 default-for-new), or undefined if not seeded. */
export const findDefaultCategory = (
	categories: CampaignDisplayCategory[],
): CampaignDisplayCategory | undefined =>
	categories.find((c) => c.name === DEFAULT_CATEGORY_NAME);

export interface CategorySelectOption {
	id: number;
	name: string;
}

/**
 * Options for a category <Select>: real categories (alphabetical), optionally with the virtual
 * Uncategorized bucket appended (sentinel id), and optionally excluding one category (used by the
 * Move-&-Delete destination picker so a category can't be its own move target).
 */
export const categorySelectOptions = (
	categories: CampaignDisplayCategory[],
	opts?: { includeUncategorized?: boolean; excludeId?: number },
): CategorySelectOption[] => {
	let reals: CategorySelectOption[] = sortCategoriesAlphabetically(categories).map((c) => ({
		id: c.id,
		name: c.name,
	}));
	if (opts?.excludeId != null) {
		reals = reals.filter((c) => c.id !== opts.excludeId);
	}
	if (opts?.includeUncategorized) {
		reals.push({ id: UNCATEGORIZED_ID, name: UNCATEGORIZED_NAME });
	}
	return reals;
};

/**
 * Category <Select> value (sentinel-aware) → campaign FK (null for the Uncategorized bucket).
 * `undefined` (the shared Select's built-in blank/clear option, which this picker never intends
 * to offer a real choice for) is treated the same as the sentinel — Uncategorized, not "no FK
 * update" — so an accidental blank selection can't leave campaigns pointing at a deleted category.
 */
export const categoryValueToFk = (value: number | undefined): number | null =>
	value === UNCATEGORIZED_ID || value === undefined ? null : value;

/** Campaign FK (null/undefined = Uncategorized) → category <Select> value (sentinel-aware). */
export const categoryFkToValue = (fk: number | null | undefined): number =>
	fk == null ? UNCATEGORIZED_ID : fk;
